import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponseHeaders, Method } from 'axios';
import * as BeMaccaferriStoreHelper from '../app/api/store/BeMaccaferriStoreHelper';
import { logDebug } from '../app/utils/utils';

export interface IRequest<T = unknown> {
    url: string;
    baseURL: string;
    data?: T;
}

export interface IResponse<D = unknown> {
    data: D;
    status: number;
    statusText: string;
    headers: TResponseHeaders;
    config: IRequestConfig;
    request?: unknown;
}

export type TRequestHeaders = AxiosRequestHeaders;

type IRequestConfig = AxiosRequestConfig;

type TResponseHeaders = AxiosResponseHeaders;

type TMethod = Method;

const generateToken = async () => {
    // Environment-based authentication logic:
    // - Local development: Call backend /api/auth/login
    // - Dev/Prod (cloud): JWT should come from eDesign Identity, throw error
    const environment = process.env.REACT_APP_ENVIRONMENT || 'local';

    if (environment === 'local') {
        // LOCAL: Call backend login endpoint
        logDebug('HttpClient', '🔄 Local environment - calling generateToken');
        await BeMaccaferriStoreHelper.generateToken();
        logDebug('HttpClient', '✅ generateToken completed - cookie set by backend');
    } else {
        // DEV/PROD: JWT should already exist from eDesign Identity
        const errorMessage = `Authentication required. Please login via eDesign Identity. (Environment: ${environment})`;
        logDebug('HttpClient', `❌ ${errorMessage}`);
        throw new Error(errorMessage);
    }
};
//${isProductionEnv() || isDevelopmentEnv() ? 'strict' : 'none'}
const httpClient = () => {
    const _instance = axios.create({
        timeout: parseInt(process.env.TIMEOUT_HTTPCLIENT as string) || 30000,
        withCredentials: true, // FIXED: Always send cookies (was: checkCookie('macToken'))
    });

    const _createHeaders = (headers?: TRequestHeaders) => {
        return {
            'Content-Type': headers?.contentType || 'application/json',
            ...headers,
        };
    };

    const _createRequest = <T = unknown>(method: TMethod, request: IRequest<T>, headers?: TRequestHeaders): AxiosRequestConfig<T> => {
        return {
            method: method,
            baseURL: request.baseURL,
            url: request.url,
            data: request.data,
            headers: _createHeaders(headers),
        };
    };

    let tokenRefreshPromise: Promise<void> | null = null;

    _instance.interceptors.response.use(
        res => res,
        async error => {
            if (error.response?.status === 401 || error.response?.status === 403) {
                const requestUrl = (error.config?.baseURL || '') + (error.config?.url || '');

                // Skip retry for auth endpoints (prevent self-deadlock) and Identity API
                if (requestUrl.includes('identity.maccaferri.com') || requestUrl.includes('auth/login')) {
                    return Promise.reject(error);
                }

                // Skip if this request was already retried (prevent infinite retry loops)
                if (error.config._retried) {
                    return Promise.reject(error);
                }

                // Shared promise pattern: first 401 triggers token refresh,
                // concurrent 401s await the same promise then retry their own request
                if (!tokenRefreshPromise) {
                    logDebug('HttpClient', '🔄 Auto-login attempt - calling generateToken');
                    tokenRefreshPromise = generateToken().finally(() => {
                        tokenRefreshPromise = null;
                    });
                }

                try {
                    await tokenRefreshPromise;
                    error.config._retried = true;
                    return _instance.request(error.config);
                } catch (retryError) {
                    logDebug('HttpClient', '❌ Auth retry failed - BLOCKED redirect to prevent data loss');
                    alert('⚠️ Session expired. Please refresh the page and login again.');
                    return Promise.reject(error);
                }
            }
            return Promise.reject(error);
        }
    );

    const getAsync = async <D = unknown>(request: IRequest<unknown>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('get', request, headers));
    };

    const postAsync = async <T = unknown, D = unknown>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('post', request, headers));
    };

    const putAsync = async <T = unknown, D = unknown>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('put', request, headers));
    };

    const deleteAsync = async <D = unknown>(request: IRequest<unknown>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('delete', request, headers));
    };

    return {
        getAsync,
        postAsync,
        putAsync,
        deleteAsync,
    };
};

export default httpClient();
